import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Stack,
  Text,
  Flex,
  HStack,
  ButtonGroup,
} from "@chakra-ui/react";
import mapboxgl, { MapEventType } from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Geometry } from "geojson";
import { t } from "i18next";
import { mapActive, mapUtils } from "../../map/utils/map-utils";
import { drawTrajectoryStyles } from "../../pages/map/views/trajectoryMapStyles";
import { screenGeoJsonMapSource } from "../../map/sources/screenGeoJsonMapSource";
import { LoadingScreen } from "../../shared/views/LoadingScreen/LoadingScreen";
import { KeyboardHint } from "../../shared/views/KeyboardHint/KeyboardHint";
import { useStores } from "../../shared/stores/RootStore";
import { toast } from "../../shared/views/CustomToaster";

const ScreenPolygonMapBase: React.FC = () => {
  const { screenStore } = useStores();
  const [map, setMap] = useState<mapboxgl.Map>();
  const [draw, setDraw] = useState<MapboxDraw>();
  const [newRing, setNewRing] = useState<Geometry | null>(null);
  const [isFetchingRouteList, setIsFetchingRouteList] = useState(false);

  /**
   * Initialize map
   */
  useEffect(() => {
    const map = new mapboxgl.Map(mapUtils.defaultConfig);

    map.on("load", () => {
      map.resize();
      screenGeoJsonMapSource.config(map!);

      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true,
        },
        styles: drawTrajectoryStyles,
      });
      setMap(map);
      setDraw(draw);
      map.addControl(draw as any);
      map.addControl(new mapboxgl.NavigationControl());

      map.on("draw.create" as MapEventType, updateArea);
      map.on("draw.delete" as MapEventType, updateArea);
      map.on("draw.update" as MapEventType, updateArea);

      function updateArea() {
        const geoJson = draw.getAll();
        const rings = geoJson.features.map((feat) => feat.geometry);
        setNewRing(rings.length ? rings[0] : null);
      }

      screenGeoJsonMapSource.configOsaWaySelection(map, (osaWayName) =>
        screenStore.selectedWays.includes(osaWayName)
          ? screenStore.removeSelectedRoute(osaWayName)
          : screenStore.addSelectedWay(osaWayName),
      );
    });
    return () => {
      map.remove();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapActive(map) && screenStore.waysResponse?.geoJson) {
      const processedGeoJson = screenGeoJsonMapSource.applyColors(
        screenStore.waysResponse.geoJson,
        screenStore.selectedWays,
      );
      mapUtils.centerMap(
        map!,
        (bounds) =>
          screenGeoJsonMapSource.extendBounds(bounds, processedGeoJson),
        {},
      );
      screenGeoJsonMapSource.update(map!, processedGeoJson);
    }
    // eslint-disable-next-line
  }, [map, screenStore.waysResponse, screenStore.selectedWays]);

  const getListByPolygonProvided = async () => {
    setIsFetchingRouteList(true);
    newRing &&
      (await screenStore.searchPolygon(newRing).then(() => {
        setIsFetchingRouteList(false);
        if (screenStore.waysResponse) {
          toast("success", t("screen:screenRouteList.success"));
        } else {
          toast("error", t("screen:screenRouteList.error"));
        }
      }));
  };

  const resetMapAndRings = () => {
    const oldGeoJson = draw!.getAll();
    for (const feat of oldGeoJson.features) {
      draw!.delete(feat.id!.toString());
    }
    screenStore.resetState();
    setNewRing(null);
    screenGeoJsonMapSource.resetConfig(map!);
    screenGeoJsonMapSource.config(map!);
  };

  return (
    <Flex flexDirection={"column"} width={"100%"} height={"100vh"}>
      {isFetchingRouteList && (
        <Box zIndex={3}>
          <LoadingScreen />
        </Box>
      )}
      <Box>
        <Text p={4}>{t("screen:map.description")}</Text>
      </Box>
      <Box flexGrow={1}>
        <Box id="map" height="100%" width="100%">
          <Stack
            position="absolute"
            top={2}
            left={2}
            zIndex={1}
            fontFamily="body"
            pointerEvents="none"
          >
            <KeyboardHint
              label={t("map:designStage.hints")}
              keys={["ctrl", "drag"]}
            />
          </Stack>
        </Box>
      </Box>
      <HStack justifyContent={"flex-end"} p={4}>
        <ButtonGroup colorPalette="primary">
          <Button onClick={resetMapAndRings} disabled={!newRing}>
            {t("screen:map.reset")}
          </Button>
          <Button
            onClick={getListByPolygonProvided}
            disabled={!(newRing && !screenStore.waysResponse)}
          >
            {t("screen:map.submit")}
          </Button>
        </ButtonGroup>
      </HStack>
    </Flex>
  );
};

export const ScreenPolygonMap = observer(ScreenPolygonMapBase);
